import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  createUser,
  editProfilePicture,
  getAllGPanelRoles,
  getAllOrgUnitsFromGoogleQuery,
  getDomains,
  getWelcomeTemplates,
} from '../../../api/users-api';
import { AddUserGroup, Policy, User } from '../../../../types';
import {
  DirectoryRoles,
  ToolsRoles,
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import SimpleFormPageNew from '../../../shared/simple-form-page-new';
import { getSchemaKeys } from '../../../shared/simple-form-page';
import { pagesConfig } from '../../../shared/pages-config';
import { StepConfig } from '../../../shared/shared-form-stepper-new';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../shared/unsaved-changes-dialog';

import ChangeLogDialog from '../../../shared/change-log-dialog';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ModuleList } from '../../../shared/analytics/module-list';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { useStore } from '../../state/zustand-store';
import { Messages } from '../../../utils/message-constants';
import { handleErrors } from '../../../utils/errors';
import { isEntityAlreadyExistsError } from '../../../utils/error-utils';
import { useNavigate } from 'react-router-dom';
import StepSetupUser from './form-steps/step-setup-user';
import StepGroups from './form-steps/step-groups';
import StepEmployeeInformation from './form-steps/step-employee-information';
import StepProfileInformation from './form-steps/step-profile-information';
import StepWelcomeRecipients from './form-steps/step-welcome-recipients';
import StepPolicies from './form-steps/step-policies';
import StepIntegrations from './form-steps/step-integrations';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  getUserFormSchema,
  GroupsSchema,
  InformationSchema,
  PoliciesSchema,
  SetupUserSchema,
  UserFormValues,
  WelcomeRecipientSchema,
  IntegrationsSchema,
} from './user-schema';
import StepReview from './form-steps/step-review';
import UserFormSkeleton from './form-steps/user-form-skeleton';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import StepGroupsIndexed from './form-steps/step-groups-indexed';
import Box from '@mui/material/Box';
import { replaceWelcomeTokens } from '../../administration/settings/welcome-templates/welcome-template-tokens';

interface WelcomeEmailModel {
  sendEmail: boolean;
  templateType: string;
  templateSubject: string;
  templateHtml: string;
  availableTemplates: string[];
}
export interface UserFormData extends Partial<User> {
  gPanelRole?: string;
  cPanelRole?: string;
  userName?: string;
  domain?: string;
  confirmPassword?: string;
  groups?: AddUserGroup[];
  usersToNotify?: string[];
  ccTo?: string[];
  bccTo?: string[];
  welcomeEmailModel?: WelcomeEmailModel;
  policies?: Policy[];
  includeCreatedUser?: boolean;
}

export default function UserFormPage() {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();

  const {
    userRoles,
    directoryRoles,
    toolsRoles,
    changeLogRoles,
    canSeePasswords,
    features,
    currentTier,
  } = useUserSettings();

  let steps: StepConfig[] = [
    { label: 'Setup User', schema: SetupUserSchema },
    { label: 'Groups', schema: GroupsSchema },
    { label: 'Employee Information' },
    { label: 'Profile Information', schema: InformationSchema },
    { label: 'Welcome Recipients', schema: WelcomeRecipientSchema },
    { label: 'Policies', schema: PoliciesSchema },
    { label: 'Integrations', schema: IntegrationsSchema },
    { label: 'Review' },
  ];

  if (!isAbleToSeeUIElement(userRoles, UserRoles.EditProfileOnCreation.value)) {
    steps = steps.filter(
      (step) =>
        step.label !== 'Profile Information' &&
        step.label !== 'Employee Information'
    );
  }

  if (
    !isAbleToSeeUIElement(directoryRoles, DirectoryRoles.AddMemberToGroup.value)
  ) {
    steps = steps.filter((step) => step.label !== 'Groups');
  }

  if (
    !isAbleToSeeUIElement(toolsRoles, ToolsRoles.ViewPolicies.value) ||
    !isAbleToSeeUIElement(toolsRoles, ToolsRoles.MaunalRunPolicy.value)
  ) {
    steps = steps.filter((step) => step.label !== 'Policies');
  }

  if (!isFeatureEnabled('slackIntegration', features)) {
    steps = steps.filter((step) => step.label !== 'Integrations');
  }

  const { data: orgUnits, isLoading: isLoadingOrgUnits } = useQuery(
    getAllOrgUnitsFromGoogleQuery()
  );

  const { data: domains, isLoading: isLoadingDomains } = useQuery({
    queryKey: [UserKeys.Domains],
    queryFn: () => getDomains(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('domains'),
    },
  });

  const { data: gPanelRoles, isLoading: isLoadingGpanelRoles } = useQuery({
    queryKey: [UserKeys.gPanelRoles],
    queryFn: () => getAllGPanelRoles(),
    meta: {
      errorMessage:
        'There was an issue loading gPanel roles. Please refresh and try again.',
    },
  });

  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { primaryDomain } = useUserSettings();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [isUserNameManual, setIsUserNameManual] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [stepCompleted, setStepCompleted] = useState<boolean[]>([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ]);

  const { data: welcomeTemplates, isLoading: isLoadingEmailTemplates } =
    useQuery({
      queryKey: [UserKeys.EmailTemplates],
      queryFn: () => getWelcomeTemplates(),
      meta: {
        errorMessage:
          'There was an issue loading email templates. Please refresh and try again.',
      },
    });

  const userDefault = welcomeTemplates?.results.find(
    (template) => template.default
  );

  const {
    control,
    handleSubmit,
    reset,
    formState,
    formState: { isDirty, isValidating, isSubmitting, errors },
    setValue,
    getValues,
    setError,
    clearErrors,
    trigger,
    watch,
  } = useForm<UserFormValues>({
    resolver: zodResolver(getUserFormSchema(activeStep, queryClient)),
    reValidateMode: 'onSubmit',
    values: {
      userName: '',
      domain:
        primaryDomain.length !== 0 ? primaryDomain : domains ? domains[0] : '',
      password: '',
      confirmPassword: '',
      name: {
        givenName: '',
        familyName: '',
        fullName: '',
      },
      includeInGlobalAddressList: true,
      changePasswordAtNextLogin: true,
      cPanelRole: 'user',
      gPanelRole: 'User',
      orgUnitPath: '/',
      groups: [],
      organizations: [],
      phones: [],
      addresses: [],
      emails: [],
      externalIds: [],
      notes: '',
      websites: [],
      relations: [],
      gender: {
        type: '',
        addressMeAs: '',
      },
      ims: [],
      welcomeEmailModel: {
        sendEmail: false,
        templateType: '',
        templateSubject: '',
        templateHtml: '',
        availableTemplates: [
          ...(welcomeTemplates?.results.map((template) =>
            template.id.toString()
          ) || []),
        ],
      },
      usersToNotify: [],
      policies: [],
      ccTo: [],
      bccTo: [],
      includeCreatedUser: false,
      scheduledSendDate: null,
    },
  });

  const domain = watch('domain');
  const email = `${watch('userName')}@${watch('domain')}`;
  const firstName = watch('name.givenName');
  const lastName = watch('name.familyName');
  const password = watch('password');
  const handleCancel = () => {
    navigate(`/${pagesConfig.users.link}`, {
      state: { fromCancelButton: true },
    });
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: Partial<User>) => {
      await createUser(
        data,
        getValues('gPanelRole') || 'User',
        (getValues('groups') || []).map((group) => ({
          ...group,
          member: {
            ...group.member,
            email: `${getValues('userName')}@${getValues('domain')}`,
          },
        })),
        getValues('welcomeEmailModel.sendEmail'),
        {
          recipients: getValues('usersToNotify') || [],
          subject: getValues('welcomeEmailModel.templateSubject'),
          body: getValues('welcomeEmailModel.templateHtml'),
          ccs: getValues('ccTo') || [],
          bccs: getValues('bccTo') || [],
          createdTime: 0,
          primaryDomain: primaryDomain,
          scheduledSendDate: (() => {
            const scheduledDate = getValues('scheduledSendDate');
            return scheduledDate ? scheduledDate.getTime() : null;
          })(),
        },
        getValues('policies') || [],
        changeLogMessage,
        getValues('slackChannels') || []
      );

      if (!profilePhoto) return { photoUploadFailed: false };

      try {
        await editProfilePicture(data.primaryEmail!, profilePhoto, '');
        return { photoUploadFailed: false };
      } catch {
        return { photoUploadFailed: true };
      }
    },
    onSuccess: ({ photoUploadFailed }, data) => {
      setSnackbar({
        open: true,
        message: photoUploadFailed
          ? Messages.ProfilePicture.CreatedUserUploadFailed
          : 'User created successfully!',
        severity: photoUploadFailed ? 'warning' : 'success',
      });

      if (profilePhoto) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.UserPhoto, data.primaryEmail],
        });
        createEvent(analytics.user_management.user_details.update_profile_pic, {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.name,
        });
      }

      queryClient.invalidateQueries({ queryKey: [UserKeys.UserList] });
      setChangeLogMessage('');
      setActiveStep(0);
      setIsUserNameManual(false);
      setProfilePhoto(null);
      reset();
      bypass();
      navigate(`/${pagesConfig.users.link}`);
    },
    onError: (error) => {
      if (isEntityAlreadyExistsError(error)) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.UserNameAvailability],
        });
        setActiveStep(0);
        setError('userName', {
          type: 'manual',
          message: Messages.UserName.JustTaken,
        });
        return;
      }

      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Creating('a user'),
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_grid.create_user, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.name,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function mutateData() {
    const correctedOrgs = getValues('organizations')?.map((org) => {
      if (org.type === 'unknown') {
        delete org.type;
        org.customType = 'Unknown';
      } else if (org.type === 'custom') {
        org.type = 'unknown';
      } else if (org.type !== 'custom' && org.customType !== '') {
        org.customType = '';
      }
      return org;
    });

    const gPanelUser = {
      ...getValues(),
      isAdmin: getValues('cPanelRole') === 'admin',
      primaryEmail: `${getValues('userName')}@${getValues('domain')}`,
      organizations: correctedOrgs || [],
      externalIds: getValues('externalIds') || [],
      phones: getValues('phones') || [],
      relations: getValues('relations') || [],
    };

    // Remove unneeded fields
    delete gPanelUser.userName;
    delete gPanelUser.cPanelRole;
    delete gPanelUser.gPanelRole;
    delete gPanelUser.domain;
    delete gPanelUser.confirmPassword;
    delete gPanelUser.groups;
    delete gPanelUser.welcomeEmailModel;
    delete gPanelUser.usersToNotify;
    delete gPanelUser.policies;
    delete gPanelUser.ccTo;
    delete gPanelUser.bccTo;
    delete gPanelUser.includeCreatedUser;
    delete gPanelUser.scheduledSendDate;
    delete gPanelUser.slackChannels;

    if (gPanelUser.gender?.type === '') {
      gPanelUser.gender = null;
    }

    gPanelUser.externalIds?.map((id) => {
      if (id.type !== 'custom' && id.customType !== '') {
        id.customType = '';
      }
    });

    gPanelUser.phones?.map((phone) => {
      if (phone.type !== 'custom' && phone.customType !== '') {
        phone.customType = '';
      }
    });

    gPanelUser.relations?.map((rel) => {
      if (rel.type !== 'custom' && rel.customType !== '') {
        rel.customType = '';
      }
    });

    // set primary as first element for each array & clean up customType if needed
    if (gPanelUser.websites && gPanelUser.websites.length > 0) {
      gPanelUser.websites.map((website, index) => {
        const isFirst = index === 0;
        website.primary = isFirst;

        if (website.type !== 'custom' && website.customType !== '') {
          website.customType = '';
        }
      });
    }

    if (gPanelUser.emails && gPanelUser.emails.length > 0) {
      gPanelUser.emails.map((email, index) => {
        const isFirst = index === 0;
        email.primary = isFirst;

        if (email.type !== 'custom' && email.customType !== '') {
          email.customType = '';
        }
      });
    }

    if (gPanelUser.ims && gPanelUser.ims.length > 0) {
      gPanelUser.ims.map((im, index) => {
        const isFirst = index === 0;
        im.primary = isFirst;

        if (im.type !== 'custom' && im.customType !== '') {
          im.customType = '';
        }
      });
    }

    if (gPanelUser.addresses && gPanelUser.addresses.length > 0) {
      gPanelUser.addresses.map((address, index) => {
        const isFirst = index === 0;
        address.primary = isFirst;

        if (address.type !== 'custom' && address.customType !== '') {
          address.customType = '';
        }
      });
    }

    gPanelUser.creationTime = new Date().getTime();
    return gPanelUser;
  }

  const onSubmit: SubmitHandler<UserFormValues> = (formData) => {
    const isProfileEditOnCreation = !!(
      formData.addresses?.length ||
      formData.emails?.length ||
      formData.externalIds?.length ||
      formData.ims?.length ||
      formData.organizations?.length ||
      formData.phones?.length ||
      formData.relations?.length ||
      formData.websites?.length ||
      formData.notes?.trim() ||
      formData.gender?.type ||
      formData.gender?.addressMeAs
    );
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.CreateUser.value) ||
      (isProfileEditOnCreation &&
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.EditProfileOnCreation.value
        ))
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const gPanelUser = mutateData();
      mutate(gPanelUser);
    }
  };

  const formStepperProps = {
    activeStep,
    setActiveStep,
    steps,
    errors: errors,
    trigger,
    stepCompleted,
    setStepCompleted,
  };

  const isLoading =
    isLoadingEmailTemplates ||
    isLoadingGpanelRoles ||
    isLoadingOrgUnits ||
    isLoadingDomains;

  function setWelcomeTemplate() {
    const selectedTemplateId = getValues('welcomeEmailModel.templateType');
    const template =
      welcomeTemplates?.results.find(
        (item) => item.id.toString() === selectedTemplateId
      ) ?? userDefault;

    if (template) {
      const tokenValues = {
        domain,
        firstName,
        lastName,
        email,
        password,
        groupEmails: (getValues('groups') || []).map((group) => group.groupId),
      };
      setValue('welcomeEmailModel.templateType', template.id.toString());
      setValue(
        'welcomeEmailModel.templateSubject',
        replaceWelcomeTokens(template.templateSubject, tokenValues, 'text')
      );
      setValue(
        'welcomeEmailModel.templateHtml',
        replaceWelcomeTokens(template.templateHtml, tokenValues)
      );
    }
  }

  function isTemplateInputStep(stepIndex: number) {
    return ['Setup User', 'Groups'].includes(steps[stepIndex]?.label);
  }

  const handleDefaultNext = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    if (steps.length - 1 === activeStep) return;
    if (isTemplateInputStep(activeStep)) setWelcomeTemplate();
    const fields = steps[activeStep].schema
      ? getSchemaKeys(steps[activeStep].schema as any)
      : [];
    const isValid = await trigger(fields as any);

    if (isValid) {
      setActiveStep((prevState) => prevState + 1);
      setStepCompleted((prevState) => {
        const newCompleted = [...prevState];
        newCompleted[activeStep] = true;
        return newCompleted;
      });
    }
  };

  const handleDefaultBack = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();

    if (isTemplateInputStep(activeStep)) setWelcomeTemplate();
    const fields = steps[activeStep].schema
      ? getSchemaKeys(steps[activeStep].schema as any)
      : [];
    await trigger(fields as any);
    setActiveStep((prevState) => prevState - 1);
    setStepCompleted((prevState) => {
      const newCompleted = [...prevState];
      newCompleted[activeStep] = true;
      return newCompleted;
    });
  };

  async function onStepClick(stepIndex: number) {
    if (stepIndex === activeStep || !stepCompleted[stepIndex]) return;
    if (isTemplateInputStep(activeStep)) setWelcomeTemplate();
    if (steps[activeStep].schema) {
      const schema = steps[activeStep].schema;
      const fields = getSchemaKeys(schema as any);
      await trigger(fields as any);
    }
    setActiveStep(stepIndex);
    setStepCompleted((prev) => {
      const newCompleted = [...prev];
      newCompleted[activeStep] = true;
      return newCompleted;
    });
  }

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty || !!profilePhoto);

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: pagesConfig.users.name, path: pagesConfig.users.link },
      ]}
    >
      <SimpleFormPageNew
        title={pagesConfig.createUser.name}
        {...formStepperProps}
        content={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isLoading && <UserFormSkeleton />}
            {!isLoading && steps[activeStep].label === 'Setup User' && (
              <StepSetupUser
                domains={domains || []}
                control={control}
                formState={formState}
                setValue={setValue}
                getValues={getValues}
                clearErrors={clearErrors}
                isUserNameManual={isUserNameManual}
                onUserNameManualChange={setIsUserNameManual}
                canSeePasswords={canSeePasswords}
                gPanelRoles={gPanelRoles || []}
                orgUnits={orgUnits || []}
                userRoles={userRoles}
                directoryRoles={directoryRoles}
                profilePhoto={profilePhoto}
                onProfilePhotoChange={setProfilePhoto}
              />
            )}
            {!isLoading &&
              steps[activeStep].label === 'Groups' &&
              (currentTier === 'enterprise' &&
              isFeatureEnabled('indexGroupsReact', features) ? (
                <StepGroupsIndexed
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  getValues={getValues}
                />
              ) : (
                <StepGroups
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  getValues={getValues}
                />
              ))}
            {!isLoading &&
              steps[activeStep].label === 'Employee Information' && (
                <StepEmployeeInformation
                  watch={watch}
                  setValue={setValue}
                  directoryRoles={directoryRoles}
                />
              )}
            {!isLoading &&
              steps[activeStep].label === 'Profile Information' && (
                <StepProfileInformation
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  formState={formState}
                  watch={watch}
                  directoryRoles={directoryRoles}
                />
              )}
            {!isLoading && steps[activeStep].label === 'Welcome Recipients' && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StepWelcomeRecipients
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  watch={watch}
                  isLoading={isLoadingEmailTemplates}
                  templates={welcomeTemplates?.results || []}
                />
              </LocalizationProvider>
            )}
            {!isLoading && steps[activeStep].label === 'Policies' && (
              <StepPolicies setValue={setValue} watch={watch} />
            )}
            {!isLoading && steps[activeStep].label === 'Integrations' && (
              <StepIntegrations
                control={control}
                setValue={setValue}
                watch={watch}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Review' && (
              <StepReview
                selectedUser={getValues()}
                templates={welcomeTemplates?.results || []}
                profilePhoto={profilePhoto}
              />
            )}
          </Box>
        }
        handleDiscard={handleCancel}
        handleSubmit={handleSubmit(onSubmit)}
        handleNext={handleDefaultNext}
        handleBack={handleDefaultBack}
        handleStepClick={onStepClick}
        submitButtonText={pagesConfig.createUser.name}
        isLoadingSubmit={isValidating || isSubmitting || isPending}
        isDisableSubmit={isLoading}
      />
      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="User"
        pageMode="create"
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const gPanelUser = mutateData();
          mutate(gPanelUser);
        }}
        mutateLoading={isPending}
      />
    </SimpleBreadcrumbsPage>
  );
}
